// ============================================================
// AdminMotivationPage — M11.C + M24 UX overhaul (2026-04-24)
// ============================================================
// PRD §6.1.O R-P0-O9 — admin chỉnh copy động viên NV theo 6 event.
//
// Mỗi event có ≥ 1 variant string. Khi event fire (đậu/trượt thi,
// xong phiên flashcard, unlock bộ mới,...), BE random-pick 1 variant
// và interpolate {placeholder} → text NV nhìn thấy.
//
// Placeholder: {name} {score} {threshold} {bo} {reward}
//   {cards_done} {min_target} {max_target} {mode}
//   {streak_days} {next_bo}
//
// M24 (2026-04-24): bỏ JSON editor thô. Form-style per-event:
//   - Mỗi event là 1 khung màu riêng, tiêu đề + emoji icon
//   - Mỗi variant là 1 textarea tự grow, có nút xoá
//   - Nút "+ Thêm lời động viên"
//   - Toolbar emoji (click chèn tại cursor)
//   - Placeholder chips (click chèn tại cursor)
//   - Tab "JSON" còn lại chỉ để xem/debug (read-only)
// ============================================================

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { hasPermission } from '../lib/permissions.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';

const EVENT_META = [
  {
    key: 'exam_pass',
    label: 'Đậu bài thi bộ câu hỏi',
    emoji: '🎉',
    hint: 'Fire khi NV đậu thi 3 câu 1 bộ (score ≥ threshold, ví dụ 90+).',
    color: 'emerald',
    placeholders: ['name', 'score', 'threshold', 'bo', 'reward'],
  },
  {
    key: 'exam_fail',
    label: 'Trượt bài thi bộ câu hỏi',
    emoji: '💪',
    hint: 'Fire khi NV trượt thi (score < threshold). Nên động viên thay vì chê.',
    color: 'amber',
    placeholders: ['name', 'score', 'threshold', 'bo'],
  },
  {
    key: 'rules_exam_pass',
    label: 'Đậu thi Nội quy',
    emoji: '📘',
    hint: 'Fire khi NV đậu thi nội quy (≥ 90% câu đúng).',
    color: 'blue',
    placeholders: ['name', 'score', 'threshold'],
  },
  {
    key: 'flashcard_session_done',
    label: 'Xong phiên flashcard',
    emoji: '🃏',
    hint: 'Fire khi NV xong 1 phiên học flashcard (learn hoặc review).',
    color: 'purple',
    placeholders: ['name', 'cards_done', 'min_target', 'max_target', 'mode'],
  },
  {
    key: 'streak_milestone',
    label: 'Mốc streak ngày học',
    emoji: '🔥',
    hint: 'Fire khi NV học liên tiếp 3/7/14/30 ngày (P1, có thể chưa wire).',
    color: 'rose',
    placeholders: ['name', 'streak_days'],
  },
  {
    key: 'reward_unlock',
    label: 'Mở khoá bộ câu hỏi mới',
    emoji: '🔓',
    hint: 'Fire khi NV đậu 1 bộ → unlock bộ kế (bo_1000k → bo_1250k → bo_nang_cao).',
    color: 'indigo',
    placeholders: ['name', 'bo', 'next_bo', 'reward'],
  },
];

// Tailwind không chấp nhận dynamic class — phải map cứng
const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', chip: 'bg-emerald-100 text-emerald-800' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-900',   chip: 'bg-amber-100 text-amber-800' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-900',    chip: 'bg-blue-100 text-blue-800' },
  purple:  { bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-900',  chip: 'bg-purple-100 text-purple-800' },
  rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-900',    chip: 'bg-rose-100 text-rose-800' },
  indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-900',  chip: 'bg-indigo-100 text-indigo-800' },
};

// 12 emoji phổ biến nhất — click là chèn tại cursor
const QUICK_EMOJIS = ['🎉', '👏', '💪', '⭐', '🔥', '🏆', '✨', '🎊', '😄', '👍', '💡', '❤️'];

const PREVIEW_SAMPLE_VARS = {
  name: 'An',
  score: 92,
  threshold: 90,
  bo: 'bo_1000k',
  reward: '50000',
  cards_done: 25,
  min_target: 20,
  max_target: 30,
  mode: 'learn',
  streak_days: 7,
  next_bo: 'bo_1250k',
};

export default function AdminMotivationPage() {
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const [tab, setTab] = useState('edit'); // edit | preview | json | help
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null); // {copies, is_default, default}

  // M24 state — thay jsonText bằng object copies
  const [copies, setCopies] = useState({}); // { event_key: [variant1, variant2, ...] }
  const [dirty, setDirty] = useState(false);

  const [previewEvent, setPreviewEvent] = useState('exam_pass');
  const [previewVars, setPreviewVars] = useState(PREVIEW_SAMPLE_VARS);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Ref tới textarea cuối cùng focus → để emoji/placeholder chèn đúng chỗ
  const lastFocusRef = useRef(null);

  useEffect(() => {
    if (!hasPermission(user, 'motivation')) {
      navigate('/admin');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // M24 (2026-04-24): apiJson trả raw body trực tiếp, KHÔNG wrap {ok,data}.
  // Các handler dưới đây dùng res.* trực tiếp và catch exception cho HTTP 4xx/5xx.

  async function load() {
    setLoading(true);
    try {
      const res = await api.motivationGet();
      // res = { copies, is_default, event_keys, default, placeholders }
      setData(res);
      const normalized = {};
      for (const m of EVENT_META) {
        const arr = res.copies?.[m.key];
        normalized[m.key] = Array.isArray(arr) ? [...arr] : [];
      }
      setCopies(normalized);
      setDirty(false);
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Không tải được');
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    // Validate FE: mỗi variant ≤ 500 ký tự, trim rỗng bỏ qua. BE M24 đã
    // cho phép event rỗng → runtime fallback default (admin không cần
    // viết đủ 6 event).
    const cleaned = {};
    for (const m of EVENT_META) {
      const arr = (copies[m.key] || [])
        .map(s => String(s).trim())
        .filter(s => s.length > 0);
      if (arr.some(s => s.length > 500)) {
        toast.error(`Event "${m.label}" có câu > 500 ký tự — rút gọn lại`);
        return;
      }
      cleaned[m.key] = arr;
    }
    setSaving(true);
    try {
      const res = await api.motivationUpdate({ copies: cleaned });
      // res = { ok: true, saved: true, variants_count }
      toast.success(`Đã lưu (${res.variants_count ?? 0} lời động viên tuỳ chỉnh)`);
      await load();
    } catch (err) {
      const head = err?.data?.error || err?.message || 'Lỗi lưu';
      const detail = err?.data?.detail ? `: ${err.data.detail}` : '';
      toast.error(head + detail);
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    const ok = await showConfirm({
      title: 'Reset copy động viên',
      message: 'Reset về copy mặc định? Mọi thay đổi sẽ mất.',
      confirmText: 'Reset',
      dangerous: true,
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.motivationReset();
      toast.success('Đã reset về mặc định');
      await load();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Lỗi reset');
    } finally {
      setSaving(false);
    }
  }

  async function loadDefaults() {
    if (!data?.default) return;
    if (copies && Object.values(copies).some(a => a.length)) {
      const ok = await showConfirm({
        title: 'Tải bản mặc định',
        message: 'Thay toàn bộ bằng bản mặc định?\n(Chưa lưu — vẫn có thể huỷ trước khi bấm Lưu)',
        confirmText: 'Tải mặc định',
      });
      if (!ok) return;
    }
    const normalized = {};
    for (const m of EVENT_META) {
      const arr = data.default[m.key];
      normalized[m.key] = Array.isArray(arr) ? [...arr] : [];
    }
    setCopies(normalized);
    setDirty(true);
    toast('Đã load bản mặc định (chưa lưu)');
  }

  // ---- Helpers thao tác trên 1 event ----
  const updateVariant = useCallback((eventKey, idx, value) => {
    setCopies(prev => {
      const arr = [...(prev[eventKey] || [])];
      arr[idx] = value;
      return { ...prev, [eventKey]: arr };
    });
    setDirty(true);
  }, []);

  const addVariant = useCallback((eventKey) => {
    setCopies(prev => ({ ...prev, [eventKey]: [...(prev[eventKey] || []), ''] }));
    setDirty(true);
  }, []);

  const removeVariant = useCallback((eventKey, idx) => {
    setCopies(prev => {
      const arr = [...(prev[eventKey] || [])];
      arr.splice(idx, 1);
      return { ...prev, [eventKey]: arr };
    });
    setDirty(true);
  }, []);

  // Chèn text tại cursor của textarea đang focus (hoặc append nếu không có)
  const insertAtCursor = useCallback((text) => {
    const ta = lastFocusRef.current;
    if (!ta || !ta.dataset?.eventKey || ta.dataset?.idx == null) {
      toast('Bấm vào 1 ô văn bản trước, rồi thử lại');
      return;
    }
    const eventKey = ta.dataset.eventKey;
    const idx = Number(ta.dataset.idx);
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const current = ta.value;
    const next = current.slice(0, start) + text + current.slice(end);
    updateVariant(eventKey, idx, next);
    // Move cursor sau text chèn
    setTimeout(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    }, 10);
  }, [updateVariant]);

  const variantsCount = useMemo(() => {
    return Object.values(copies).reduce(
      (s, arr) => s + (Array.isArray(arr) ? arr.filter(x => String(x).trim()).length : 0),
      0
    );
  }, [copies]);

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Đang tải…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Trang admin</Link>
          <h1 className="text-xl md:text-2xl font-bold mt-1">Copy động viên NV</h1>
          <p className="text-sm text-slate-600 mt-1">
            {data?.is_default
              ? <span className="text-amber-600">Đang dùng copy mặc định (chưa tuỳ chỉnh)</span>
              : <span className="text-green-600">Đã tuỳ chỉnh — {variantsCount} lời động viên</span>}
            {dirty && <span className="ml-2 text-rose-600">• có thay đổi chưa lưu</span>}
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b mb-4">
        {[
          { k: 'edit',    label: '✏️ Biên soạn' },
          { k: 'preview', label: '👀 Xem trước' },
          { k: 'json',    label: '📋 JSON' },
          { k: 'help',    label: '❓ Hướng dẫn' },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium ${tab === t.k ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-600 hover:text-slate-900'}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'edit' && (
        <div className="space-y-5">
          {/* Toolbar chung: emoji quick insert */}
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b pb-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-slate-600 mr-1">Chèn icon:</span>
              {QUICK_EMOJIS.map(e => (
                <button
                  key={e}
                  onClick={() => insertAtCursor(e)}
                  className="px-2 py-1 text-lg hover:bg-slate-100 rounded transition"
                  title={`Chèn ${e} tại con trỏ`}
                >{e}</button>
              ))}
              <span className="ml-auto text-xs text-slate-500">
                Gõ bình thường • Emoji paste được • Bấm icon/placeholder để chèn tại con trỏ
              </span>
            </div>
          </div>

          {/* 6 event block */}
          {EVENT_META.map(meta => (
            <EventBlock
              key={meta.key}
              meta={meta}
              variants={copies[meta.key] || []}
              onUpdate={(i, v) => updateVariant(meta.key, i, v)}
              onAdd={() => addVariant(meta.key)}
              onRemove={(i) => removeVariant(meta.key, i)}
              onFocus={(el) => { lastFocusRef.current = el; }}
              onInsertPlaceholder={(p) => insertAtCursor(`{${p}}`)}
            />
          ))}

          {/* Actions */}
          <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t pt-3 pb-2 flex flex-wrap gap-2">
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="bg-blue-600 text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700 transition"
            >{saving ? 'Đang lưu…' : `💾 Lưu ${variantsCount} lời động viên`}</button>
            <button
              onClick={loadDefaults}
              disabled={saving}
              className="bg-slate-100 px-4 py-2.5 rounded-lg text-sm hover:bg-slate-200 disabled:opacity-50"
            >Load mẫu mặc định</button>
            <button
              onClick={reset}
              disabled={saving}
              className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-lg text-sm hover:bg-rose-100 disabled:opacity-50 ml-auto"
            >Reset về mặc định (xoá tuỳ chỉnh)</button>
          </div>
        </div>
      )}

      {tab === 'preview' && (
        <PreviewTab
          previewEvent={previewEvent}
          setPreviewEvent={setPreviewEvent}
          previewVars={previewVars}
          setPreviewVars={setPreviewVars}
          previewResult={previewResult}
          previewLoading={previewLoading}
          onRun={async () => {
            setPreviewLoading(true);
            try {
              // res = { event, vars, variants, count }
              const res = await api.motivationPreview(previewEvent, previewVars);
              setPreviewResult(res);
            } catch (err) {
              toast.error(err?.data?.error || err?.message || 'Lỗi preview');
            } finally {
              setPreviewLoading(false);
            }
          }}
        />
      )}

      {tab === 'json' && (
        <div className="space-y-2">
          <div className="text-sm text-slate-600">
            JSON chỉ đọc — phục vụ export/backup. Muốn chỉnh thì quay lại tab Biên soạn.
          </div>
          <pre className="bg-slate-50 border rounded-lg p-4 text-xs overflow-auto max-h-[600px]">{JSON.stringify(copies, null, 2)}</pre>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(JSON.stringify(copies, null, 2));
              toast.success('Đã copy JSON vào clipboard');
            }}
            className="bg-slate-100 px-3 py-1.5 rounded text-sm hover:bg-slate-200"
          >Copy JSON</button>
        </div>
      )}

      {tab === 'help' && <HelpTab />}
    </div>
  );
}

// ============================================================
// EventBlock — 1 khung màu cho 1 event, list variant textarea
// ============================================================
function EventBlock({ meta, variants, onUpdate, onAdd, onRemove, onFocus, onInsertPlaceholder }) {
  const c = COLOR_MAP[meta.color];
  return (
    <div className={`border ${c.border} ${c.bg} rounded-xl p-4`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{meta.emoji}</span>
          <div>
            <div className={`font-semibold ${c.text}`}>{meta.label}</div>
            <div className="text-xs text-slate-600 mt-0.5">{meta.hint}</div>
          </div>
        </div>
        <div className={`text-xs px-2 py-1 rounded-full ${c.chip} font-medium whitespace-nowrap`}>
          {variants.filter(v => String(v).trim()).length} lời
        </div>
      </div>

      {/* Placeholder chips — click chèn {name} etc vào textarea đang focus */}
      {meta.placeholders.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          <span className="text-xs text-slate-500 mr-1 py-1">Chèn biến:</span>
          {meta.placeholders.map(p => (
            <button
              key={p}
              onClick={() => onInsertPlaceholder(p)}
              className="text-xs px-2 py-0.5 bg-white border rounded font-mono text-slate-700 hover:bg-slate-100"
              title={`Chèn {${p}} tại con trỏ`}
            >{`{${p}}`}</button>
          ))}
        </div>
      )}

      {/* List variant */}
      <div className="space-y-2">
        {variants.length === 0 && (
          <div className="text-sm text-slate-500 italic p-3 border border-dashed border-slate-300 rounded bg-white">
            Chưa có lời động viên nào. Bấm "+ Thêm" bên dưới.
          </div>
        )}
        {variants.map((v, i) => (
          <VariantRow
            key={i}
            value={v}
            index={i}
            eventKey={meta.key}
            onChange={(val) => onUpdate(i, val)}
            onRemove={() => onRemove(i)}
            onFocus={onFocus}
          />
        ))}
      </div>

      <button
        onClick={onAdd}
        className={`mt-3 text-sm px-3 py-1.5 border border-dashed ${c.border} ${c.text} rounded-lg hover:bg-white transition`}
      >
        + Thêm lời động viên
      </button>
    </div>
  );
}

// ============================================================
// VariantRow — 1 textarea + nút xoá
// ============================================================
function VariantRow({ value, index, eventKey, onChange, onRemove, onFocus }) {
  const taRef = useRef(null);

  // Autosize textarea
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.max(44, ta.scrollHeight) + 'px';
  }, [value]);

  const overLimit = value && value.length > 500;
  const counter = value?.length || 0;

  return (
    <div className="flex gap-2 items-start">
      <div className="flex-1 relative">
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => onFocus?.(e.target)}
          data-event-key={eventKey}
          data-idx={index}
          placeholder="Ví dụ: 🎉 Tuyệt vời {name}! Điểm {score}/100, đậu bộ {bo} rồi 💪"
          rows={2}
          className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${overLimit ? 'border-rose-400 ring-rose-300' : 'border-slate-300'}`}
          style={{ minHeight: 44 }}
        />
        <div className={`absolute bottom-1 right-2 text-[10px] pointer-events-none ${overLimit ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>
          {counter}/500
        </div>
      </div>
      <button
        onClick={onRemove}
        className="text-rose-600 hover:bg-rose-50 p-2 rounded transition"
        title="Xoá lời động viên này"
      >🗑️</button>
    </div>
  );
}

// ============================================================
// PreviewTab — chọn event + gõ vars → render all variant đã interpolate
// ============================================================
function PreviewTab({ previewEvent, setPreviewEvent, previewVars, setPreviewVars, previewResult, previewLoading, onRun }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <label className="flex flex-col">
          <span className="text-xs text-slate-600 mb-1">Event</span>
          <select
            value={previewEvent}
            onChange={e => setPreviewEvent(e.target.value)}
            className="border rounded px-2 py-2 text-sm"
          >
            {EVENT_META.map(e => (
              <option key={e.key} value={e.key}>{e.emoji} {e.label}</option>
            ))}
          </select>
        </label>
        <button
          onClick={onRun}
          disabled={previewLoading}
          className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50 hover:bg-blue-700 text-sm"
        >{previewLoading ? 'Đang preview…' : '👀 Xem tất cả variant'}</button>
      </div>
      <div>
        <div className="text-xs text-slate-600 mb-2">Vars mẫu (có thể sửa):</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.keys(previewVars).map(k => (
            <label key={k} className="text-xs flex flex-col">
              <span className="text-slate-500 font-mono">{`{${k}}`}</span>
              <input
                type="text"
                value={previewVars[k]}
                onChange={e => setPreviewVars(v => ({ ...v, [k]: e.target.value }))}
                className="border rounded px-2 py-1 text-sm"
              />
            </label>
          ))}
        </div>
      </div>

      {previewResult && (
        <div className="mt-4">
          <div className="text-sm font-semibold mb-2">
            {previewResult.count} variant cho <code className="bg-slate-100 px-1">{previewResult.event}</code>:
          </div>
          <ul className="space-y-2">
            {previewResult.variants.map((v, i) => (
              <li key={i} className="bg-blue-50 border border-blue-200 rounded p-3 text-sm whitespace-pre-wrap">
                <span className="text-xs text-blue-600 mr-2">#{i + 1}</span>
                {v}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================
// HelpTab
// ============================================================
function HelpTab() {
  return (
    <div className="prose prose-sm max-w-none">
      <h3>6 event — khi nào hệ thống hiện lời động viên?</h3>
      <div className="space-y-2 not-prose">
        {EVENT_META.map(m => {
          const c = COLOR_MAP[m.color];
          return (
            <div key={m.key} className={`border ${c.border} ${c.bg} rounded p-3`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">{m.emoji}</span>
                <span className={`font-semibold ${c.text}`}>{m.label}</span>
                <code className="text-xs text-slate-500">{m.key}</code>
              </div>
              <div className="text-xs text-slate-600 mt-1">{m.hint}</div>
              <div className="text-xs mt-1">
                <span className="text-slate-500">Biến: </span>
                {m.placeholders.map(p => (
                  <code key={p} className="bg-white border px-1 mx-0.5 rounded text-slate-700">{`{${p}}`}</code>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="mt-6">Cách viết cho hay</h3>
      <ul className="text-sm">
        <li><strong>Ngắn gọn:</strong> 1-2 câu, tối đa 500 ký tự.</li>
        <li><strong>Có tên NV:</strong> dùng <code>{'{name}'}</code> để câu trở nên cá nhân hoá.</li>
        <li><strong>Emoji vừa phải:</strong> 1-2 emoji / câu, đặt ở đầu hoặc cuối.</li>
        <li><strong>Viết đa dạng:</strong> 3-5 variant / event để mỗi lần NV thấy khác nhau.</li>
        <li><strong>Trượt thi → khích lệ:</strong> tránh chê bai, nhấn mạnh "thử lại".</li>
      </ul>

      <h3 className="mt-4">Ví dụ thực tế</h3>
      <div className="space-y-2 not-prose text-sm">
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
          <span className="text-xs font-semibold text-emerald-900">🎉 Đậu thi</span>
          <div className="mt-1">🎉 Xuất sắc {'{name}'}! {'{score}'}/100 điểm — đậu {'{bo}'} rồi 💪</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-3">
          <span className="text-xs font-semibold text-amber-900">💪 Trượt thi</span>
          <div className="mt-1">Chưa đạt lần này {'{name}'} ơi ({'{score}'}/100). Xem lại đáp án và thử lại nhé ✨</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded p-3">
          <span className="text-xs font-semibold text-purple-900">🃏 Xong phiên flashcard</span>
          <div className="mt-1">🃏 {'{cards_done}'} thẻ hôm nay, chế độ {'{mode}'}. Mai học tiếp nhé!</div>
        </div>
      </div>
    </div>
  );
}
